/**
 * ForecastEquationStrip — the summary strip, read as an equation (05.3 D-13c).
 *
 *     Committed  +  Upside  +  Managed Services  =  Total
 *
 * quick-260921-f73 (John: "keep artifact and dashboard aligned") — the middle
 * terms now match the artifact's strip (v25+): Committed is the PRODUCT deals
 * that carry no toggle (Closed Won + Commit + Most Likely at 1.00), Upside is
 * the product Best Case call at the viewer's %, Managed Services is its own
 * ARR stream whole. The earlier Product New / Product Expansion split moved to
 * the Deal Detail table's Type column.
 *
 * This is the artifact's spine for the tab (D-13a) and the reason the page opens
 * on a figure an exec can check: the three displayed stream figures SUM to the
 * displayed total at any Upside %, because all four come from a single
 * `streams()` partition rather than from four independent passes over `deals`.
 * A second pass could disagree with the first by a rounding step or, worse, by a
 * deal that fell through both partitions — and the one thing an equation cannot
 * survive is not adding up.
 *
 * D-13d — no explanatory filler. No line narrates what a figure counts toward;
 * the label carries the figure. quick-260921-f73 took this one step further: the
 * per-stream `won · commit · upside` legs line is gone, so each stream is a
 * label and a figure and nothing else (John, 2026-09-21).
 *
 * D-06 — the budget reading is a percentage AND a raw signed over/under, green
 * above budget and red below, using the design system's existing good/bad
 * tokens. No new visual language (05.1 UI-SPEC).
 *
 * v39 (John, 2026-10-01): every figure, label and operator on the strip is one
 * size up (text-2xl figures, text-xs labels); the percent-of-budget line under
 * them keeps its size on purpose. Mirrors the artifact v39.
 *
 * quick-260917-nfy — the strip wears the artifact's `.combined` tint: a
 * brand-100 background inside a 1px brand-300 border (Card supplies the border
 * width; the `!` prefix is this file's override idiom over Card's default
 * `border-ink-100 bg-white`). The right-hand stream is labelled 'Total', no
 * period suffix, exactly as the artifact v25–v28 prints it.
 */
import { streams } from '../../lib/forecast-weighting.js';
import { formatAmount, pctOf, signed } from '../../lib/format.js';
import { Card } from '../ui/Card.js';

import type { ForecastDeal } from '@saledash/shared';
import type { UpsidePct } from '@saledash/shared/forecast/weighting';

interface ForecastEquationStripProps {
  deals: readonly ForecastDeal[];
  /**
   * The viewer's Upside % (D-03), RECEIVED from the page. The strip never
   * sources the stored default itself — the page is the sole supplier, which is
   * what keeps the strip, the section headline and the deal-table footer on one
   * number.
   */
  upsidePct: UpsidePct;
  /** The period's single ARR budget (D-05); null ⇒ no reading to give. */
  target: number | null;

}

interface StreamSpec {
  slug: string;
  label: string;
  value: number;
}

export function ForecastEquationStrip({
  deals,
  upsidePct,
  target,
}: ForecastEquationStripProps): React.JSX.Element {
  const st = streams(deals, upsidePct);

  // The artifact's three terms (its `q.committed` / `q.upside` / `q.managed`
  // cells): product won + commit, product Best Case at the toggle, Managed whole.
  const specs: StreamSpec[] = [
    {
      slug: 'committed',
      label: 'Committed',
      value: st.newBiz.won + st.newBiz.commit + st.expansion.won + st.expansion.commit,
    },
    { slug: 'upside', label: 'Upside', value: st.newBiz.upside + st.expansion.upside },
    { slug: 'managed', label: 'Managed Services', value: st.managed.total },
  ];

  // `combinedArr` comes from the SAME partition that produced the three splits,
  // so the equation holds exactly rather than approximately.
  const combined = st.combinedArr;
  const pct = pctOf(combined, target);
  const delta = target === null ? null : combined - target;

  return (
    <Card data-testid="forecast-equation-strip" className="!border-2 !border-brand-300 !bg-brand-100">
      <div
        className="flex flex-wrap items-center justify-between gap-3"
        data-testid="forecast-equation-header"
      >
        <p className="text-[0.72rem] font-semibold uppercase tracking-wider text-brand-700">
          Combined ARR
        </p>
      </div>

      <div className="mt-2 flex flex-wrap items-start gap-x-4 gap-y-3">
        {specs.map((spec, i) => (
          <div key={spec.slug} className="flex items-start gap-4">
            {i > 0 && (
              <span aria-hidden className="pt-7 text-3xl font-light text-ink-300">
                +
              </span>
            )}
            <StreamFigure spec={spec} />
          </div>
        ))}

        <span aria-hidden className="pt-7 text-3xl font-light text-ink-300">
          =
        </span>

        <div data-testid="forecast-stream-combined">
          <p className="text-xs font-medium uppercase tracking-wider text-ink-400">
            Total
          </p>
          <p
            className="text-2xl font-semibold tabular-nums text-ink-900"
            data-testid="forecast-combined-arr"
          >
            {formatAmount(combined)}
          </p>
        </div>
      </div>

      <div
        className="mt-3 flex flex-wrap items-baseline gap-x-2 border-t border-ink-100 pt-2 text-xs text-ink-500"
        data-testid="forecast-combined-budget"
      >
        {pct === null || delta === null ? (
          /* quick-261001-ibu — a quarter with no seeded ARR budget (FY2027-Q1
             while the FY2027 targets are TBD) says so rather than showing a
             bare dash that reads like a data fault. */
          <span className="text-ink-700" data-testid="forecast-combined-budget-tbd">
            Budget TBD
          </span>
        ) : (
          <>
            <span className="tabular-nums text-ink-700" data-testid="forecast-combined-budget-pct">
              {pct}
            </span>
            <span>
              of the{' '}
              <span className="tabular-nums text-ink-700">{formatAmount(target ?? 0)}</span> ARR
              budget
            </span>
            <span aria-hidden>·</span>
            <span
              className={`font-semibold tabular-nums ${delta < 0 ? 'text-bad-text' : 'text-good-text'}`}
              data-testid="forecast-combined-budget-delta"
            >
              {signed(delta)}
            </span>
          </>
        )}
      </div>
    </Card>
  );
}

/**
 * One stream: its label and its figure — nothing underneath.
 *
 * quick-260921-f73 — the grey `won · commit · upside` legs line is GONE (John:
 * "remove that grey detail. this is a summary box"). The strip is the summary;
 * the Product card and the Deal Detail table below carry the breakdown. The
 * artifact's strip has never printed legs (v25).
 */
function StreamFigure({ spec }: { spec: StreamSpec }): React.JSX.Element {
  return (
    <div data-testid={`forecast-stream-${spec.slug}`}>
      <p className="text-xs font-medium uppercase tracking-wider text-ink-400">
        {spec.label}
      </p>
      <p
        className="text-2xl font-semibold tabular-nums text-ink-900"
        data-testid={`forecast-stream-total-${spec.slug}`}
      >
        {formatAmount(spec.value)}
      </p>
    </div>
  );
}
